import React, { useState } from "react";
import { api } from "../../utils/client";
import { useMutation } from "@tanstack/react-query";
import { LicensePlate } from "../../types";
import { Button } from "../ui";
import VehicleDetails from "../VehicleDetails";
import Spinner from "../Spinner";
import SmartForm from "../SmartForm";
import InputField from "../SmartForm/InputField";
import { object, string } from "yup";
import { Info } from "lucide-react";
import { trackEvent } from "../../utils/analytics";
import { trackMetaEvent } from "../../utils/metaPixel";

interface LicensePlateFormData {
  license_plate: string;
}

const REGEX_LICENSE_PLATE =
  /^[A-Za-z]{2}-\d{3}-[A-Za-z]{2}$|^[A-Za-z]{2}\d{3}[A-Za-z]{2}$/;
const regex = new RegExp(REGEX_LICENSE_PLATE);
const schema = object({
  license_plate: string().required("La plaque d'immatriculation est requise"),
});

const searchPlaque = async (licensePlate: string) => {
  const response = await api.get(`/license_plates/${licensePlate}`);
  const licensePlateData = response.data.data;
  if (response.data.already_exists) {
    throw {
      status: "already_exists",
      licensePlate: licensePlateData,
    };
  }
  return licensePlateData;
};

type LicensePlateStepProps = {
  onSubmit: (data: LicensePlateFormData, other?: any) => void;
  title?: string;
  // null masque la ligne d'accroche
  tagline?: React.ReactNode | null;
  buttonLabel?: React.ReactNode;
};

const defaultTagline = (
  <>
    <span className="text-primary font-bold">⏱ 2 minutes</span> · Avis certifié
    et publié immédiatement
  </>
);

const LicensePlateStep: React.FC<LicensePlateStepProps> = (props) => {
  const {
    title = "Trouvez votre modèle en un clic",
    tagline = defaultTagline,
    buttonLabel = "Rechercher",
  } = props;
  const [licensePlate, setLicensePlate] = useState<LicensePlate | null>(null);
  const [errorStatus, setErrorStatus] = useState<string | number | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: searchPlaque,
    onSuccess: (data) => {
      setLicensePlate(data);
      setErrorStatus(null);
    },
    onError: ({ status, licensePlate }) => {
      setErrorStatus(status);
      setLicensePlate(licensePlate);
    },
  });

  const onSubmit = (data: LicensePlateFormData) => {
    setErrorStatus(null);
    mutate(data.license_plate);
  };

  const handleAcceptVehicle = () => {
    if (licensePlate?.immat) {
      trackEvent({
        name: "add_to_cart",
        params: {
          category: "Formulaire avis",
          event_name: "license_plate_entered",
          page_type: "review_form",
          field: "license_plate",
        },
      });
      trackMetaEvent("InitiateCheckout", {
        content_ids: ["license_plate"],
        content_type: "product",
        page_type: "Formulaire évaluation",
        event_name: "license_plate_entered",
      });

      props.onSubmit(
        {
          license_plate: licensePlate.immat!,
        },
        {
          vehicleInfo: licensePlate,
        },
      );
    }
  };

  const handleRejectVehicle = () => {
    trackEvent({
      name: "Véhicule non trouvé dans l'évaluation",
      params: {
        category: "Interaction",
        action: "Click",
        label: "Véhicule non trouvé dans l'évaluation",
        page_type: "Formulaire évaluation",
      },
    });
    setLicensePlate(null);
    setErrorStatus(null);
  };

  if (errorStatus === 404) {
    return (
      <div className="space-y-4">
        <p>La plaque d'immatriculation n'a pas été trouvée.</p>
        <Button
          onClick={handleRejectVehicle}
          variant="default"
          className="w-full"
        >
          Nouvelle recherche
        </Button>
      </div>
    );
  }

  // Si la plaque est trouvée, afficher les informations de la voiture
  if (licensePlate) {
    return (
      <div className="space-y-4">
        <p>Est-ce bien votre véhicule ?</p>
        <div className="">
          <VehicleDetails vehicleInfo={licensePlate} />
          {errorStatus === "already_exists" ? (
            <div className="mt-4">
              <div className="text-sm text-blue-600 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md p-3">
                <Info className="size-4" />
                Un avis a déjà été soumis avec cette plaque d'immatriculation.
              </div>
              <div className="mt-4">
                <Button
                  onClick={handleRejectVehicle}
                  variant="outline"
                  className="w-full"
                >
                  Nouvelle recherche
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-col gap-4 justify-center">
                <Button
                  onClick={handleRejectVehicle}
                  variant="link"
                  className="w-full"
                >
                  Ce n'est pas mon véhicule
                </Button>
                <Button
                  onClick={handleAcceptVehicle}
                  variant="default"
                  className="w-full"
                >
                  C'est mon véhicule
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex justify-center items-center gap-4 flex-col">
        <Spinner />
        <div>
          <p>Nous recherchons votre véhicule...</p>
        </div>
      </div>
    );
  }

  const handleSearchButtonClick = () => {
    trackEvent({
      name: "Rechercher un véhicule par immatriculation",
      params: {
        category: "Interaction",
        action: "Click",
        label: "Rechercher un véhicule par immatriculation",
        page_type: "Formulaire évaluation",
      },
    });
  };

  const handleLicensePlateFocus = () => {
    trackEvent({
      name: "begin_checkout",
      params: {
        category: "Interaction",
        action: "Focus",
        label: "Focus champ immatriculation",
        page_type: "Formulaire évaluation",
      },
    });

    trackMetaEvent("AddToCart", {
      content_ids: ["license_plate_input"],
      content_type: "product",
      page_type: "Formulaire évaluation",
      event_name: "license_plate_input_focus",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <span className="text-lg font-bold text-center">{title}</span>
        {tagline && <span className="text-center">{tagline}</span>}
      </div>
      <SmartForm
        schema={schema}
        defaultValues={{
          license_plate: "",
        }}
        onSubmit={onSubmit}
      >
        <InputField
          name="license_plate"
          label="Saisissez votre numéro d'immatriculation"
          placeholder="Ex: AB123CD ou AB-123-CD"
          containerProps={{ className: "flex flex-col items-center" }}
          onFocus={handleLicensePlateFocus}
        />
        <Button
          onClick={handleSearchButtonClick}
          type="submit"
          variant="default"
          className="w-full mt-4"
          disabled={isPending}
        >
          {isPending ? "Recherche en cours..." : buttonLabel}
        </Button>
      </SmartForm>
    </div>
  );
};

export default LicensePlateStep;
